import React, { useEffect, useContext, useRef, useCallback } from 'react';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../components/editor/useEditorWithDragDrop';
import AiContentToolbar from '../../../components/TdxAi/AiContentToolbar';
import { AuthContext } from '../../../context/AuthContext';
import { uploadEditorImage } from '../../../api/common';

function SubFormApproval({ formData, setFormData, mode = 'write', docTitle = '' }) {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isWriteMode = mode === 'write';

  const { token } = useContext(AuthContext);
  const pendingImages = useRef(new Map());

  const storeImageLocally = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    pendingImages.current.set(blobUrl, file);
    return blobUrl;
  }, []);

  const editor = useEditorWithDragDrop(tiptapExtensions, formData.editorContent || '', {
    editable: !isViewMode,
    onImageUpload: storeImageLocally,
  });

  // ✅ editor 내용이 바뀔 때 formData.editorContent 업데이트 (pending 이미지 업로드 포함)
  useEffect(() => {
    if (editor && (isWriteMode || isEditMode)) {
      const updateHandler = async () => {
        let content = editor.getHTML();

        const pending = new Map(pendingImages.current);
        pendingImages.current.clear();

        for (const [blobUrl, file] of pending) {
          if (content.includes(blobUrl)) {
            try {
              const serverUrl = await uploadEditorImage(file, token, 'aprv');
              content = content.replaceAll(blobUrl, serverUrl);
            } catch (err) {
              console.error('이미지 업로드 실패:', err);
            }
          }
          URL.revokeObjectURL(blobUrl);
        }

        setFormData(prev => ({ ...prev, editorContent: content }));
      };
      editor.on('update', updateHandler);

      return () => editor.off('update', updateHandler);
    }
  }, [editor, isWriteMode, isEditMode, setFormData, token]);

  useEffect(() => {
    if (editor && (isEditMode || isWriteMode)) {
      editor.commands.setContent(formData.editorContent || '');
    }
  }, [editor, formData.editorContent, isEditMode, isWriteMode]);


  return (
    <table className="table table-bordered mt-3">
      <tbody>
        {/* AI 툴바 (view 모드에서만 표시) */}
        {isViewMode && (
          <tr>
            <td colSpan="2">
              <AiContentToolbar
                content={formData.editorContent || ''}
                title={docTitle}
              />
            </td>
          </tr>
        )}

        {/* 결재유형 */}
        <tr className="table-title">
          <td>결재 유형</td>
          <td>
            {isViewMode ? (
              <span>
                {formData.approvalType === 'normal'
                  ? '일반 결재'
                  : formData.approvalType === 'urgent'
                  ? '긴급 결재'
                  : ''}
              </span>
            ) : (
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="approvalType"
                    value="normal"
                    checked={formData.approvalType === 'normal'}
                    onChange={(e) =>
                      setFormData(prev => ({ ...prev, approvalType: e.target.value }))
                    }
                  />
                  <label className="form-check-label">일반 결재</label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="approvalType"
                    value="urgent"
                    checked={formData.approvalType === 'urgent'}
                    onChange={(e) =>
                      setFormData(prev => ({ ...prev, approvalType: e.target.value }))
                    }
                  />
                  <label className="form-check-label">긴급 결재</label>
                </div>
              </div>
            )}
          </td>
        </tr>

        {/* ✅ 내용 (웹에디터, 타이틀 없이) */}
        <tr>
          <td colSpan="2">
            {isViewMode ? (
              <div
                className="editor-content"
                dangerouslySetInnerHTML={{ __html: formData.editorContent }}
              />
            ) : (
              <div className="border p-2 rounded">
                {editor && <EditorToolbar editor={editor} onImageUpload={storeImageLocally} />}
                <EditorContent editor={editor} className="editor-content" />
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default SubFormApproval;